// ✅ src/pages/VehicleV2/VehicleSubAssemblyMaster.jsx  (FULL FILE - UPDATED WITH LOCALSTORAGE HEADERS)

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate, useSearchParams } from "react-router-dom";
import { FaArrowLeft, FaSave, FaTrash, FaEdit } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/subAssemblyMaster";

const VehicleSubAssemblyMaster = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ localStorage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId = localStorage.getItem("userId") || "";

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  // ✅ keep session fallback for selected vehicle type
  const vehTypeFromUrl = Number(searchParams.get("vehicletypeid") || 0);
  const vehTypeFromSession =
    Number(sessionStorage.getItem("VehicleTypeID") || sessionStorage.getItem("vehicletypeid") || 0);
  const initialVehicleTypeId = vehTypeFromUrl || vehTypeFromSession || 0;

  const [loading, setLoading] = useState(false);

  // like ASPX ddl disabled
  const [vehicleTypeLocked, setVehicleTypeLocked] = useState(true);

  // editing
  const [editingId, setEditingId] = useState(0);

  // dropdowns
  const [vehicleTypes, setVehicleTypes] = useState([]);

  // form
  const [formData, setFormData] = useState({
    vehicleTypeId: 0,
    subAssemblyName: "",
  });

  // list + pagination
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // selection
  const [selectedIds, setSelectedIds] = useState(new Set());

  const totalPages = useMemo(() => (total ? Math.ceil(total / limit) : 0), [total, limit]);

  const fetchDropdowns = async (vehTypeId) => {
    const r = await axios.get(`${API_BASE}/dropdowns`, {
      params: { vehicleTypeId: vehTypeId || 0 },
      headers: commonHeaders,
    });

    if (!r.data?.success) throw new Error(r.data?.message || "Failed to load dropdowns");

    const types = Array.isArray(r.data.vehicleTypes) ? r.data.vehicleTypes : [];
    setVehicleTypes(types);

    const selected =
      Number(vehTypeId || 0) ||
      Number(r.data.selectedVehicleTypeId || 0) ||
      Number(types?.[0]?.id || 0);

    setFormData((p) => ({ ...p, vehicleTypeId: selected || 0 }));
    setVehicleTypeLocked(true);
    return selected || 0;
  };

  const fetchList = async (vehTypeId, pg = page, lim = limit) => {
    if (!vehTypeId) {
      setRows([]);
      setTotal(0);
      setSelectedIds(new Set());
      return;
    }

    const r = await axios.get(`${API_BASE}/list`, {
      params: { vehicleTypeId: vehTypeId, page: pg, limit: lim },
      headers: commonHeaders,
    });

    if (!r.data?.success) throw new Error(r.data?.message || "Failed to load list");

    setRows(Array.isArray(r.data.rows) ? r.data.rows : []);
    setTotal(Number(r.data.total || 0));
    setSelectedIds(new Set());
  };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        if (vehTypeFromUrl) sessionStorage.setItem("VehicleTypeID", String(vehTypeFromUrl));

        const selected = await fetchDropdowns(initialVehicleTypeId);
        await fetchList(selected, 1, limit);
        setPage(1);
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to initialize page");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehTypeFromUrl, commonHeaders]);

  useEffect(() => {
    (async () => {
      try {
        if (!formData.vehicleTypeId) return;
        setLoading(true);
        await fetchList(formData.vehicleTypeId, page, limit);
      } catch (e) {
        console.error(e);
        toast.error(e.message || "Failed to load list");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, commonHeaders]);

  const onChange = (e) => {
    const { name, value } = e.target;

    if (name === "vehicleTypeId") {
      const v = Number(value || 0);
      setFormData((p) => ({ ...p, vehicleTypeId: v }));
      setPage(1);
      return;
    }

    setFormData((p) => ({ ...p, [name]: value }));
  };

  const clearForm = () => {
    setEditingId(0);
    setFormData((p) => ({ ...p, subAssemblyName: "" }));
  };

  const handleEdit = (row) => {
    setEditingId(Number(row.subAssemblyId || 0));
    setFormData((p) => ({
      ...p,
      subAssemblyName: row.subAssemblyName || "",
    }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = async () => {
    const vehicleTypeId = Number(formData.vehicleTypeId || 0);
    const subAssemblyName = String(formData.subAssemblyName || "").trim();

    if (!vehicleTypeId) return toast.error("Please select Vehicle Type");
    if (!subAssemblyName) return toast.error("Please enter Vehicle Sub Assembly");

    try {
      setLoading(true);

      const payload = {
        vehicleTypeId,
        subAssemblyId: editingId ? editingId : 0,
        subAssemblyName,
        branchId: Number(branchId || 0),
        loginUserId: Number(userId || 0),
      };

      const r = await axios.post(`${API_BASE}/save`, payload, {
        headers: commonHeaders,
      });

      if (!r.data?.success) {
        toast.error(r.data?.message || "Save failed");
        return;
      }

      toast.success(r.data?.message || (editingId ? "Updated Successfully" : "Saved Successfully"));
      clearForm();
      await fetchList(vehicleTypeId, page, limit);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const toggleRow = (id) => {
    const rowId = Number(id || 0);
    if (!rowId) return;

    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const toggleAll = () => {
    if (!rows.length) return;

    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => Number(r.subAssemblyId)));
    });
  };

  const handleBulkDelete = async (idsFromArgs = null) => {
    const ids = Array.isArray(idsFromArgs) ? idsFromArgs : Array.from(selectedIds);

    if (!ids.length) {
      toast.error("Please select at least one row to delete");
      return;
    }

    if (!window.confirm(`Delete ${ids.length} selected Sub Assemblies?`)) return;

    try {
      setLoading(true);
      const r = await axios.post(
        `${API_BASE}/delete`,
        { ids },
        { headers: commonHeaders }
      );

      if (!r.data?.success) {
        toast.error(r.data?.message || "Delete failed");
        return;
      }

      toast.success(r.data?.message || "Delete completed");
      await fetchList(formData.vehicleTypeId, page, limit);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.message || e.message || "Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">VEHICLE SUB ASSEMBLY MASTER</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Vehicle Sub Assembly
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {total} Records
            </span>
          </div>

          <button
            onClick={() => navigate(-1)}
            className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
              isDark
                ? "border-gray-600 text-white hover:bg-gray-800"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <FaArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>

        {/* Form */}
        <div
          className={`rounded-xl p-6 border mb-6 ${
            isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {/* Vehicle Type */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Vehicle Type
              </label>
              <select
                name="vehicleTypeId"
                value={formData.vehicleTypeId || 0}
                onChange={onChange}
                disabled={vehicleTypeLocked}
                className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                  isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"
                } ${vehicleTypeLocked ? "opacity-80 cursor-not-allowed" : ""}`}
              >
                <option value={0}>Select Vehicle Type</option>
                {vehicleTypes.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Sub Assembly */}
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                Vehicle Sub Assembly
              </label>
              <input
                name="subAssemblyName"
                value={formData.subAssemblyName}
                onChange={onChange}
                placeholder="Enter sub assembly..."
                className={`w-full px-4 py-3 rounded-lg border focus:ring-2 focus:ring-blue-500 focus:border-transparent transition ${
                  isDark
                    ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                }`}
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={clearForm}
              className="px-6 py-3 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              {editingId ? "Cancel Edit" : "Clear"}
            </button>

            <button
              onClick={handleSave}
              disabled={loading}
              className={`flex items-center gap-2 px-8 py-3 text-white rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                loading ? "bg-gray-400 cursor-not-allowed" : `${buttonClass} ${hoverClass}`
              }`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  {editingId ? "Updating..." : "Saving..."}
                </>
              ) : (
                <>
                  <FaSave className="w-4 h-4" />
                  {editingId ? "Update" : "Save"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* Actions above table */}
        <div className="flex justify-end mb-3">
          <button
            onClick={() => handleBulkDelete()}
            disabled={loading || selectedIds.size === 0}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold ${
              loading || selectedIds.size === 0 ? "bg-gray-400 cursor-not-allowed" : "bg-red-600 hover:bg-red-700"
            }`}
          >
            <FaTrash className="w-4 h-4" />
            Delete Selected
          </button>
        </div>

        {/* Table */}
        <div className={`rounded-xl shadow-sm border overflow-hidden ${isDark ? "border-gray-700" : "border-gray-200"}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-left font-semibold w-16">S.No</th>
                  <th className="px-4 py-4 text-center font-semibold w-16">
                    <input
                      type="checkbox"
                      checked={rows.length > 0 && selectedIds.size === rows.length}
                      onChange={toggleAll}
                    />
                  </th>
                  <th className="px-4 py-4 text-left font-semibold">Vehicle Sub Assembly</th>
                  <th className="px-4 py-4 text-center font-semibold w-48">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      No sub assembly found.
                    </td>
                  </tr>
                ) : (
                  rows.map((r, idx) => {
                    const id = Number(r.subAssemblyId);
                    const checked = selectedIds.has(id);

                    return (
                      <tr
                        key={id}
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                          isDark ? "bg-gray-900" : "bg-white"
                        }`}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                          {(page - 1) * limit + idx + 1}
                        </td>

                        <td className="px-4 py-3 text-center">
                          <input type="checkbox" checked={checked} onChange={() => toggleRow(id)} />
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-white">{r.subAssemblyName}</td>

                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => handleEdit(r)}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transform hover:-translate-y-0.5 transition-all duration-200"
                            >
                              <FaEdit className="w-3 h-3" />
                              Edit
                            </button>

                            <button
                              onClick={() => handleBulkDelete([id])}
                              className="inline-flex items-center gap-1 px-3 py-2 bg-red-600 text-white text-xs font-semibold rounded-lg hover:bg-red-700 transform hover:-translate-y-0.5 transition-all duration-200"
                            >
                              <FaTrash className="w-3 h-3" />
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className={`flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 border-t ${
              isDark ? "border-gray-700 bg-gray-900" : "border-gray-200 bg-white"
            }`}
          >
            <div className="text-sm">
              Page <b>{page}</b> of <b>{totalPages || 1}</b> • Total <b>{total}</b>
            </div>

            <div className="flex items-center gap-2">
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(Number(e.target.value));
                  setPage(1);
                }}
                className={`px-3 py-2 rounded-lg border ${
                  isDark ? "bg-gray-800 border-gray-700 text-white" : "bg-white border-gray-300 text-gray-900"
                }`}
              >
                {[10, 20, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n} / page
                  </option>
                ))}
              </select>

              <button
                disabled={!canPrev || loading}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className={`px-3 py-2 rounded-lg border ${
                  !canPrev || loading
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"
                }`}
              >
                Prev
              </button>

              <button
                disabled={!canNext || loading}
                onClick={() => setPage((p) => p + 1)}
                className={`px-3 py-2 rounded-lg border ${
                  !canNext || loading
                    ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                    : isDark
                    ? "bg-gray-800 border-gray-700 text-white hover:bg-gray-700"
                    : "bg-white border-gray-300 text-gray-800 hover:bg-gray-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default VehicleSubAssemblyMaster;